import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCallback } from 'react';

interface ViewModeState {
  viewModes: Record<string, string>;
  setViewMode: (pageKey: string, mode: string) => void;
}

export const useViewModeStore = create<ViewModeState>()(
  persist(
    (set) => ({
      viewModes: {},
      setViewMode: (pageKey, mode) =>
        set((state) => ({
          viewModes: { ...state.viewModes, [pageKey]: mode },
        })),
    }),
    {
      name: 'ccms_view_modes',
      storage: {
        getItem: (name) => {
          const value = localStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
      },
      partialize: (state) =>
        ({ viewModes: state.viewModes }) as unknown as ViewModeState,
    }
  )
);

/**
 * Hook for convenient per-page view mode with persistence.
 * Returns [viewMode, setViewMode] similar to useState.
 */
export function useViewMode<T extends string = string>(
  pageKey: string,
  defaultMode: T
): [T, (mode: T) => void] {
  const viewMode = useViewModeStore(
    useCallback(
      (s: ViewModeState) => (s.viewModes[pageKey] ?? defaultMode) as T,
      [pageKey, defaultMode]
    )
  );
  const setViewMode = useCallback(
    (mode: T) => useViewModeStore.getState().setViewMode(pageKey, mode),
    [pageKey]
  );
  return [viewMode, setViewMode];
}
